import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Timeline, times } from './timeline'
import { DatePicker } from '@/components/organisms/space-manager/date-picker'
import { useWindowSize } from 'react-use'
import { HEADER_HEIGHT } from '@/constants/layout'
import { NewSpace, SpaceDetail, getTime, getTimeRangeCount, TIME_LINE_WIDTH, NOW, convertEndTime } from '@/models/space-manager/timeline'
import { PanelCardList, reservedStatusColor } from './panel-card-list'
import { SpaceAsset, SpaceDetailType } from '@/models/guest-app/asset'
import uuid from 'react-uuid'
import { SpaceReservationModal } from './space-reservation-modal'
import { getReservationSpaces, getSpaceByReservation } from '@/apis/aipass'
import { ApprovedStatus } from '@/models/reservation-approved-status'
import { useQuery } from '@/hooks/use-query'
import { SpaceReservation } from '@/models/space-manager'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { LoaderContextCreator } from '@/contexts/loader'
import { SpaceReservationExportModal } from './space-reservation-export-modal'
import { AccountContext } from '@/contexts/account'

const editStatusColor = (approvedStatus: string) => {
  switch (approvedStatus) {
    case ApprovedStatus.Reservation:
      return '#F2A429'
    case ApprovedStatus.Checkin:
      return '#F47110'
    case ApprovedStatus.Stay:
      return '#3E85CC'
    case ApprovedStatus.Checkout:
      return '#7DC931'
    case 'preparation':
      return '#676767'
    default:
      return '#F2A429'
  }
}

interface AssignmentProps {
  spaceAssets: SpaceAsset[]
}
export const Assignment: React.FC<AssignmentProps> = ({ spaceAssets }) => {
  const query = useQuery()
  const windows = useWindowSize()
  const { t } = useTranslation()
  const timeRangeRef = useRef<HTMLDivElement>(null)
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { account } = useContext(AccountContext)
  const hotelId = account?.hotel?.id
  const spaceAssetCollapseStorageKey = hotelId ? `SpaceAssetCollapse-${hotelId}` : null

  const [selectedTimelineSpaces, setSelectedTimelineSpaces] = useState<NewSpace>({})
  const [spaces, setSpaces] = useState<NewSpace>({})
  const [reservedSpaces, setReservedSpaces] = useState<SpaceDetail[]>([])
  const [viewDate, setViewDate] = useState(
    query.get('usedAt') ? dayjs(`${dayjs(query.get('usedAt') as string).format('YYYY-MM-DD')} ${dayjs().format('HH:mm:ss')}`) : NOW,
  )
  const [visibleReservationExportModal, setVisibleReservationExportModal] = useState(false)
  const [visibleReservationModal, setVisibleReservationModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editReservedSpace, setEditReservedSpace] = useState<SpaceReservation[]>([])
  const [isDeleteExistReservation, setIsDeleteExistReservation] = useState(false)
  const [showPanel, setShowPanel] = useState(false)
  const [fixedReservationId, setFixedReservationId] = useState<string | undefined>(query.get('reservationId') || undefined)
  const [newlyCreatedReservationId, setNewlyCreatedReservationId] = useState<string | null>(null)
  const [hiddenSpaceAssetIds, setHiddenSpaceAssetIds] = useState<string[]>([])

  useEffect(() => {
    if (!spaceAssetCollapseStorageKey) {
      setHiddenSpaceAssetIds([])
      return
    }
    try {
      setHiddenSpaceAssetIds(JSON.parse(localStorage.getItem(spaceAssetCollapseStorageKey) || '[]'))
    } catch {
      setHiddenSpaceAssetIds([])
    }
  }, [spaceAssetCollapseStorageKey])

  const toggleSpaceAssetCollapse = (spaceAssetId: string) => {
    setHiddenSpaceAssetIds(prev => {
      const next = prev.includes(spaceAssetId) ? prev.filter(id => id !== spaceAssetId) : [...prev, spaceAssetId]
      if (spaceAssetCollapseStorageKey) {
        localStorage.setItem(spaceAssetCollapseStorageKey, JSON.stringify(next))
      }
      return next
    })
  }

  const createSpaceDetailsByFetch = (spaceReservations: SpaceReservation[]) => {
    const reservedDetails: SpaceDetail[] = []
    spaceReservations.forEach(reservationSpace => {
      reservationSpace.details.forEach(detail => {
        const startTime = dayjs(detail.usedAtFrom).format('HH:mm')
        const endTime = dayjs(detail.usedAtTo).format('HH:mm')
        const count = getTimeRangeCount({ start: startTime, end: endTime })
        const { hour, minute } = getTime(startTime)
        const left = hour * TIME_LINE_WIDTH + (minute / 60) * TIME_LINE_WIDTH
        const width = (count * TIME_LINE_WIDTH) / 4

        const space: SpaceDetail = {
          id: detail.id,
          spaceReservationId: reservationSpace.id,
          spaceTypeName: detail.reservedSpace.title.ja,
          date: dayjs(detail.usedAtFrom),
          spaceIndex: detail.reservedSpace.spaceIndex,
          usageTime: {
            usageFrom: startTime,
            usageTo: convertEndTime(endTime),
          },
          pax: detail.pax,
          answer: detail.answer,
          reservationUserName: detail.reservationUserName,
          reservation: reservationSpace.reservation,
          salesHour: findSpaceMaster(detail.reservedSpace.spaceId).salesHour,
          spaceId: detail.reservedSpace.spaceId,
          groupId: uuid(),
          style: {
            left,
            width,
            color: editStatusColor(reservationSpace.reservation.approvedStatus.toString()),
            backgroundColor: reservedStatusColor(reservationSpace.reservation.approvedStatus.toString()),
            border: '1px solid #CCCCCC',
          },
        }
        reservedDetails.push(space)
      })
    })

    return reservedDetails
  }

  const fetchReservationSpaces = async () => {
    setIsLoading(true)

    // 指定日付の予約データを取得
    const reservationSpaces = await getReservationSpaces({ day: viewDate.format('YYYY-MM-DD') })
    const existDetails = createSpaceDetailsByFetch(reservationSpaces)

    // 既存のスペースデータを保持（日付跨ぎのスペースを保持）
    const existingSpaces = Object.keys(spaces).flatMap(spaceTypeName =>
      spaces[spaceTypeName].filter(space => !space.date || !space.date.isSame(viewDate, 'day')),
    )

    // 予約データと既存のスペースデータをマージ
    const existIds = new Set(existDetails.map(d => d.id))
    const allDetails = [...existDetails, ...existingSpaces.filter(s => !existIds.has(s.id))]

    setReservedSpaces(allDetails)
    setIsLoading(false)
  }

  // 新規スペース追加
  const addSpace = (space: SpaceDetail, allowEdit?: boolean) => {
    const newSpace = {
      ...space,
      style: {
        ...space.style,
        backgroundColor: space.style.backgroundColor,
      },
      date: viewDate,
    }
    let newSpaceDetails: SpaceDetail[] = []
    if (space.spaceTypeName in spaces) {
      const existDetails = spaces[space.spaceTypeName]
      existDetails.push({
        ...newSpace,
        groupId: space.groupId,
      })
      newSpaceDetails = existDetails
    } else {
      const newSpaceDetail: SpaceDetail = {
        ...newSpace,
        groupId: uuid(),
      }
      newSpaceDetails = [newSpaceDetail]
    }

    setSpaces({ ...spaces, [space.spaceTypeName]: newSpaceDetails })

    if (allowEdit) {
      setEditMode(true)
    }

    setShowPanel(true)

    if (isDeleteExistReservation) {
      setIsDeleteExistReservation(false)
    }
  }

  // 予約済み編集
  const onEditReservedSpace = async () => {
    if (isDeleteExistReservation || !fixedReservationId) {
      return
    }

    const fetchedReservationSpaces = await getSpaceByReservation(fixedReservationId).then(res => res || [])
    const sameReservationSpaces = createSpaceDetailsByFetch(fetchedReservationSpaces)

    const newSpaces: NewSpace = sameReservationSpaces.reduce((acc, sameReservationSpace) => {
      const newSpace: SpaceDetail = {
        ...sameReservationSpace,
        style: {
          ...sameReservationSpace.style,
          backgroundColor: editStatusColor(sameReservationSpace.reservation.approvedStatus.toString()),
          color: '#fff',
          border: 'none',
        },
        groupId: sameReservationSpace.groupId,
      }
      if (sameReservationSpace.spaceTypeName in acc) {
        const details = acc[sameReservationSpace.spaceTypeName]
        acc[sameReservationSpace.spaceTypeName] = [...details, newSpace]
      } else {
        acc[sameReservationSpace.spaceTypeName] = [newSpace]
      }

      return acc
    }, {})

    setEditReservedSpace(fetchedReservationSpaces)
    setShowPanel(true)
    setSelectedTimelineSpaces(newSpaces)
    setSpaces(newSpaces)
  }

  const changeSpace = (spaceTypeName: string, newDetails: SpaceDetail[]) => {
    setSpaces({ ...spaces, [spaceTypeName]: newDetails })
  }

  const onChangeDate = (dateString: string) => {
    setViewDate(dayjs(`${dateString} ${viewDate.format('HH:mm')}`))
  }

  const onClosePanel = (clearReservationId = false) => {
    setSpaces({})
    setSelectedTimelineSpaces({})
    setShowPanel(false)
    setEditReservedSpace([])
    setEditMode(false)
    setIsDeleteExistReservation(false)
    if (clearReservationId) {
      setFixedReservationId(undefined)
    }
  }

  const onSaved = async (reservationId?: string) => {
    setVisibleReservationModal(false)
    await fetchReservationSpaces()

    // 状態リセット
    setIsDeleteExistReservation(false)
    setEditReservedSpace([])
    setEditMode(false)

    // 予約者情報が存在する場合
    const hasReservationInfo = fixedReservationId || reservationId
    if (hasReservationInfo) {
      setShowPanel(true)
      setSelectedTimelineSpaces({})
      if (reservationId) {
        setFixedReservationId(reservationId)
      }
      return
    }

    // 予約者情報がない場合：パネルを閉じる
    onClosePanel(true)
  }

  // タイムライン上の既存予約クリック時の処理
  const onClickSpaceInTimeline = async (reservationId?: string) => {
    if (reservationId) {
      await fetchReservationSpaces()
      setFixedReservationId(reservationId)
      setEditMode(false)
    } else {
      onClosePanel()
    }
  }

  // 新規予約作成完了時の処理
  const handleNewReservationCreated = async (reservationId: string) => {
    await fetchReservationSpaces()
    setFixedReservationId(reservationId)
    setEditMode(false)
    setShowPanel(true)
  }

  const removeEditReservation = (deleteSpace: SpaceDetail) => {
    const newDetails = spaces[deleteSpace.spaceTypeName]?.filter(detail => detail.groupId !== deleteSpace.groupId)

    changeSpace(deleteSpace.spaceTypeName, newDetails)

    if (deleteSpace.id) {
      setIsDeleteExistReservation(true)
    }
  }

  const findSpaceMaster = (spaceId: string): SpaceDetailType => {
    return spaceAssets.flatMap(space => space.details).find(space => space.id === spaceId) as SpaceDetailType
  }

  const spaceLimitTimes: Record<string, { start: string; end: string }> = spaceAssets
    .flatMap(space => space.details)
    .reduce((acc, detail) => {
      acc[detail.title] = detail.spacePrice?.chargePrice.usageTime
      return acc
    }, {})

  const barStyle = useMemo(() => {
    const hour = viewDate.hour() * TIME_LINE_WIDTH
    const minute = (viewDate.minute() / 60) * TIME_LINE_WIDTH

    return css({
      left: `${hour + minute}px`,
    })
  }, [viewDate])

  useLayoutEffect(() => {
    const hour = viewDate.hour()
    const scrollDistance = (hour - 1) * TIME_LINE_WIDTH

    if (timeRangeRef.current) {
      timeRangeRef.current.scrollLeft = scrollDistance
    }
  }, [timeRangeRef, viewDate])

  useEffect(() => {
    fetchReservationSpaces()
  }, [viewDate])

  // 固定予約IDの変更時の処理
  useEffect(() => {
    if (!fixedReservationId) return
    // 予約スペースが読み込まれるまで待機
    if (reservedSpaces.length === 0) return

    const exists = reservedSpaces.some(space => space.reservation?.reservationId === fixedReservationId)

    if (exists) {
      // 初回表示 or 編集
      onEditReservedSpace()
    } else {
      // 詳細
      setEditMode(false)
      setSpaces({})
      setShowPanel(true)
    }
  }, [fixedReservationId, reservedSpaces])

  return (
    <>
      <div css={assignmentWrapperStyle}>
        <div
          ref={timeRangeRef}
          className="timeline-wrapper"
          style={{
            height: windows.height - HEADER_HEIGHT - 50,
            // 324=PanelCardList.width
            width: showPanel ? 'calc(100% - 324px)' : 'auto',
          }}
        >
          <div style={{ position: 'sticky', left: 0, zIndex: 20, background: '#fff' }}>
            <div className="date-range">
              <img
                src={require(`@/static/images/arrow_left_gray.svg`)}
                css={{ width: 36, cursor: 'pointer' }}
                onClick={() => setViewDate(viewDate.subtract(1, 'day'))}
              />
              <DatePicker isFullYear date={viewDate.format('YYYY-MM-DD')} onDateChange={dateString => onChangeDate(dateString)} />
              <img
                src={require(`@/static/images/arrow_right_gray.svg`)}
                css={{ width: 36, cursor: 'pointer' }}
                onClick={() => setViewDate(viewDate.add(1, 'day'))}
              />
            </div>

            {spaceAssets.map(space => {
              const isCollapsed = hiddenSpaceAssetIds.includes(space.id)
              return (
                <div key={space.id}>
                  <button
                    type="button"
                    className="space-name"
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleSpaceAssetCollapse(space.id)}
                  >
                    {space.title}
                  </button>

                  {!isCollapsed && (
                    <ul className="space-list">
                      {space.details.map(spaceDetail =>
                        Array.from(new Array(spaceDetail.stock)).map((_, stockIndex) => (
                          <li key={`${spaceDetail.title}-${stockIndex}`}>
                            <div style={{ width: '100%' }}>
                              <div className="type-time" key={stockIndex}>
                                <p className="type-name">{stockIndex === 0 ? spaceDetail.title : ''}</p>
                                <p className="amount">{++stockIndex}</p>
                              </div>
                            </div>
                          </li>
                        )),
                      )}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>

          <div className="time-range-block">
            <div style={{ position: 'sticky', top: 0, zIndex: 19 }}>
              <p className="select-date">{viewDate.format(t('YYYY-MM-DD'))}</p>
              <ul className="time-list">
                {times.map(time => {
                  const isCurrent = viewDate.isSame(NOW, 'd') && dayjs(viewDate.format(`YYYY/MM/DD ${time}`)).isSame(viewDate, 'hour')
                  return (
                    <li key={time} className={isCurrent ? 'current' : ''}>
                      {time}
                    </li>
                  )
                })}
              </ul>
            </div>

            <div className="timeline">
              <ul>
                {spaceAssets.map((spaceAsset, index) => {
                  const isCollapsed = hiddenSpaceAssetIds.includes(spaceAsset.id)
                  return (
                    <React.Fragment key={`${spaceAsset.id}-${index}`}>
                      <li style={{ background: '#FAFAFA' }} />
                      {!isCollapsed &&
                        spaceAsset.details.map((spaceDetail, spaceDetailIndex) => {
                          return Array.from(new Array(spaceDetail.stock)).map((v, stockIndex) => {
                            const spaceIndex = ++stockIndex
                            const spacesByDate = spaces[spaceDetail.title]?.filter(({ date }) => date?.isSame(viewDate, 'day')) || []
                            const selectedTimelineSpacesBySomeDate =
                              selectedTimelineSpaces[spaceDetail.title]?.filter(({ date }) => date?.isSame(viewDate, 'day')) || []
                            const sameReservedSpaces = reservedSpaces
                              .filter(reservedSpace => reservedSpace.date?.isSame(viewDate, 'day'))
                              .filter(
                                reservedSpace =>
                                  reservedSpace.spaceTypeName === spaceDetail.title && reservedSpace.spaceIndex === spaceIndex,
                              )

                            return (
                              <li key={`${spaceDetail.title}-${spaceDetailIndex}-${stockIndex}`} css={timelineStyle} className="unit-list">
                                <Timeline
                                  spaceIndex={spaceIndex}
                                  spaceId={spaceDetail.id}
                                  salesHour={spaceDetail.salesHour}
                                  usageTime={spaceDetail.spacePrice?.chargePrice.usageTime}
                                  editingTimelineSpaces={spacesByDate}
                                  selectedTimelineSpaces={selectedTimelineSpacesBySomeDate}
                                  allReservedSpaces={sameReservedSpaces}
                                  typeName={spaceDetail.title}
                                  editMode={editMode}
                                  addSpace={(space: SpaceDetail, allowEdit?: boolean) => addSpace(space, allowEdit)}
                                  removeEditReservation={removeEditReservation}
                                  onClickSpaceInTimeline={onClickSpaceInTimeline}
                                />
                              </li>
                            )
                          })
                        })}
                    </React.Fragment>
                  )
                })}
              </ul>
              {viewDate.isSame(NOW, 'day') && (
                <div className="current-time-bar" css={barStyle}>
                  <div>
                    <span></span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {showPanel && (
          <PanelCardList
            editMode={editMode}
            onSaved={onSaved}
            editReservedSpace={editReservedSpace}
            setEditMode={setEditMode}
            onChangeDate={onChangeDate}
            isDeleteExistReservation={isDeleteExistReservation}
            spaces={spaces}
            setVisibleReservationModal={setVisibleReservationModal}
            reservedSpaces={reservedSpaces}
            changeSpace={changeSpace}
            onCancelEdit={onClosePanel}
            onClosePanel={onClosePanel}
            removeEditReservation={removeEditReservation}
            spaceLimitTimes={spaceLimitTimes}
            fixedReservationId={fixedReservationId}
            setIsDeleteExistReservation={setIsDeleteExistReservation}
          />
        )}
        <div className={['operation-block', showPanel ? 'show-panel' : ''].join(' ')}>
          <Button
            buttonType={ButtonType.NonStyle}
            width={130}
            height={32}
            fontSize={12}
            icon={require('@/static/images/csv.svg')}
            onClick={() => setVisibleReservationExportModal(true)}
          >
            {t('Download')}
          </Button>
        </div>
      </div>

      {visibleReservationModal && (
        <SpaceReservationModal
          date={viewDate}
          spaces={spaces}
          onClose={async (newReservationId?: string) => {
            setVisibleReservationModal(false)
            if (newReservationId) {
              await handleNewReservationCreated(newReservationId)
            }
          }}
        />
      )}
      {visibleReservationExportModal && (
        <SpaceReservationExportModal date={viewDate} spaceAssets={spaceAssets} onClose={() => setVisibleReservationExportModal(false)} />
      )}
    </>
  )
}

const assignmentWrapperStyle = css({
  position: 'relative',
  '.timeline-wrapper': {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'relative',
    background: '#fff',
    boxShadow: '0px 0px 6px #0000001A',
    borderRadius: 5,
    overflow: 'auto',
  },
  '.date-range': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    height: 95,
    borderRight: '1px solid #F2F2F2',
    position: 'sticky',
    top: 0,
    '.DateInput_input': {
      width: 124,
      fontWeight: 'bold',
      letterSpacing: 0.7,
    },
  },
  '.select-date': {
    padding: '11px 16px',
    height: 32,
    fontSize: 11,
    fontWeight: 'bold',
    background: '#FAFAFA',
  },
  '.date .DateInput_input': {
    fontSize: 12,
    fontWeight: 'bold',
  },
  '.space-name': {
    appearance: 'none',
    border: 'none',
    width: '100%',
    height: 42,
    fontSize: 12,
    fontWeight: 'bold',
    fontFamily: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    paddingBlock: '17px',
    paddingLeft: 24,
    background: '#FAFAFA',
    cursor: 'pointer',
  },
  '.time-list': {
    display: 'flex',
    width: '100%',
    height: 63,
    borderTop: '1px solid #F2F2F2',
    background: '#fff',
    li: {
      width: TIME_LINE_WIDTH,
      fontSize: 14,
      fontWeight: 'bold',
      position: 'relative',
      flex: 1,
      padding: '25px 16px',
      borderRight: '1px solid #F2F2F2',
      '&.current': { color: '#F2A40B' },
    },
  },
  '.timeline': {
    position: 'relative',
    '.current-time-bar': {
      position: 'absolute',
      width: 2,
      height: '100%',
      background: '#F2A40B',
      top: 0,
      zIndex: 18,
      '> div': {
        position: 'relative',
        width: '100%',
        height: '100%',
        span: {
          width: 14,
          height: 9,
          background: '#F2A40B',
          position: 'absolute',
          clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          top: 0,
          left: -6,
        },
      },
    },
    li: {
      height: 42,
      borderTop: '1px solid #F2F2F2',
      '&:last-of-type': { borderBottom: '1px solid #F2F2F2' },
    },
  },
  '.space-list': {
    li: {
      display: 'flex',
      height: 42,
      borderTop: '1px solid #F2F2F2',
      '&:last-of-type': { borderBottom: '1px solid #F2F2F2' },
    },
    '.type-name': {
      width: 174,
      height: '100%',
      fontSize: 12,
      fontWeight: 'bold',
      paddingBlock: 17,
      paddingLeft: 24,
    },
    '.amount': {
      width: 37,
      textAlign: 'center',
      paddingBlock: 17,
      borderLeft: '1px solid #F2F2F2',
      borderRight: '1px solid #F2F2F2',
    },
    '.type-time': {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      fontSize: 12,
      fontWeight: 'bold',
    },
  },
  '.SingleDatePickerInput': {
    border: 'none',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '.SingleDatePickerInput_calendarIcon': { display: 'none' },
  },
  '.SingleDatePicker_picker.SingleDatePicker_picker__directionLeft': { top: '32px !important' },
  '.SingleDatePickerInput__withBorder, .DateInput ': { width: 120 },
  '.operation-block': {
    position: 'absolute',
    top: 0,
    right: 16,
    zIndex: 20,
    '&.show-panel': { right: 16 + 324 },
  },
})

const timelineStyle = css({
  display: 'flex',
  width: '100%',
  height: '100%',
  position: 'relative',
  '.unit': {
    display: 'inline-block',
    width: '25%',
    height: '100%',
    cursor: 'pointer',
    borderRight: '1px #F2F2F2',
    '&.overtime': {
      background: '#FAFAFA',
      pointerEvents: 'none',
    },
  },
})
